// Раздел «Дела»: очередь того, что требует решения, и ничего сверх.
//
// Это второй экран «утра финансового директора». Первый — пульт подотчёта, он отвечает на
// вопрос «у кого сколько висит». «Дела» отвечают на другой: «что я должен сделать сегодня».
//
// Три правила, из которых собран экран:
//
// **Дело называет число, сумму и следующий шаг.** Строка без числа — это напоминание, а не
// дело; строка без перехода заставляет искать самому, и человек перестаёт сюда заходить.
//
// **Пусто — это состояние с причиной.** «Ничего не ждёт решения» пишется словами, а не пустым
// полем: белый экран читается как поломка загрузки.
//
// **Дел с нулём не бывает.** Очередь из девяти пунктов, во всех ноль, перестают читать целиком
// — вместе с тем единственным, где не ноль.

import { useRef } from 'react';

import { RiArrowRightSLine, RiCheckboxCircleLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import { formatRubles } from '@/shared/money/format';
import { countLabel, pluralRu } from '@/shared/text/plural';
import { formatDataAsOf, parseMoment } from '@/shared/time/format';
import { ListSkeleton } from '@/shared/ui/list-row';
import { Подсказка } from '@/shared/hints/Подсказка';
import { SectionHeader } from '@/shared/ui/section-header';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';

import { useAuthStore } from '@/shared/auth/store';

import type { ДелаСводка } from './queries';
import { useCases, источникиПоПравам } from './queries';

/**
 * Из чего собрана очередь ИМЕННО У ЭТОГО человека — фразой для пустого состояния.
 *
 * Пока текст был один на всех, он врал: табельщику, у которого нет подотчёта вовсе, экран
 * сообщал «чеки приняты, деньги в полях не зависли, транши проведены». Ни одного чека он не
 * видит по правам, и такая фраза не успокаивает, а сбивает — человек начинает искать, где же
 * эти чеки. Пустое состояние обязано называть причину (`ПРАВИЛА.md`, правило 7), а причина у
 * разных ролей разная.
 */
function чтоПроверено(
  permissions: readonly string[],
  сводка: ДелаСводка | undefined,
): string {
  const источники = источникиПоПравам(permissions);
  const части: string[] = [];
  // Каждая часть говорит, чего НЕТ, а не что сделано. «Транши проведены» при пустом
  // официальном контуре утверждало работу, которой не было вовсе, а «долгов за уволенными нет»
  // — то, чего программа не знает, пока старые долги не перенесены (сверка 23.09.2026).
  if (источники.подотчёт) {
    части.push('непринятых чеков и зависших денег в полях нет');
  }
  if (источники.люди) {
    части.push('долгов за уволенными в программе нет');
  }
  if (источники.контур) {
    части.push('непроведённых траншей нет');
  }
  if (источники.тарифы) {
    части.push('тариф за прошлый месяц заполнен у всех');
  }
  // Только когда список правда ответил нулём. Упал или старый сервер его не знает — молчим:
  // «все на постах» без проверки успокаивало бы ровно тогда, когда тридцать человек выпали.
  if (источники.безПоста && сводка?.безПостаПроверен === true) {
    части.push('все люди на вахте стоят на постах');
  }
  if (части.length === 0) {
    return 'Здесь появится всё, что потребует внимания, — сразу с числом и суммой.';
  }
  const перечисление = части.join(', ');
  const начало = перечисление[0]!.toUpperCase() + перечисление.slice(1);
  return `${начало}. Здесь появится всё, что потребует внимания, — сразу с числом и суммой.`;
}

export function CasesSection() {
  const дела = useCases();
  const якорьДел = useRef<HTMLDivElement>(null);
  const permissions = useAuthStore((state) => state.permissions);

  return (
    <main className='flex h-full flex-1 flex-col overflow-hidden bg-bg-weak-50'>
      {/* Заголовок раздела стоит ПЕРВЫМ и живёт независимо от данных: до 25.08.2026 строка
          вопроса открывала экран, и человек видел поле ввода раньше, чем название раздела, в
          который вошёл. Порядок чтения простой: где я → что здесь → чем спросить. */}
      <div ref={якорьДел}>
        <SectionHeader
          title='Дела'
          subtitle={
            дела.data === undefined
              ? undefined
              : `${countLabel(дела.data.дела.length, ['дело ждёт', 'дела ждут', 'дел ждут'])} решения`
          }
          aside={
            срезДанных(дела.data) === null
              ? undefined
              : formatDataAsOf(срезДанных(дела.data) as Date)
          }
        />
      </div>
      <Подсказка урок='дела-это-очередь' якорь={якорьДел} />
      <ScreenState
        data={дела.data}
        error={дела.error as ApiError | null}
        subject='очередь дел'
        isEmpty={(данные) => данные.дела.length === 0}
        onRetry={() => void дела.refetch()}
        loading={
          <div className='p-5'>
            <ListSkeleton rows={4} />
          </div>
        }
        empty={
          <div className='flex h-full items-center justify-center'>
            <EmptyState
              icon={RiCheckboxCircleLine}
              title='Ничего не ждёт решения'
              illustration='спокойно'
              description={чтоПроверено(permissions, дела.data)}
            />
          </div>
        }
      >
        {(данные) => {
          return (
            <>
              {/* Дела лежат КАРТОЧКАМИ, а не строками во всю ширину.
                  До 03.09.2026 здесь было три строки, растянутые на 1680 точек, и под ними
                  семьсот точек пустоты. Строка во всю ширину годится списку, который длинный
                  и однородный: глаз идёт по левому краю сверху вниз. Дел же три-пять, они
                  разные, и каждое читается целиком — тогда строка работает против чтения:
                  между заголовком слева и суммой справа полтора метра пустого места, и
                  связать их глазом стоит усилия.

                  Карточка собирает всё дело в один прямоугольник: что случилось, сколько
                  людей, на сколько денег, куда идти. Три таких помещаются в ряд и занимают
                  экран, а не жмутся к его верхнему краю. */}
              <ul className='grid flex-1 auto-rows-min grid-cols-1 content-start gap-4 overflow-y-auto p-5 md:grid-cols-2 2xl:grid-cols-3'>
                {данные.дела.map((дело) => (
                  <li key={дело.код} className='min-w-0'>
                    <Link
                      to={дело.адрес}
                      className='group flex h-full flex-col gap-3 rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-4 shadow-regular-xs transition duration-200 ease-out hover:border-stroke-sub-300 hover:shadow-regular-sm'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <span className='min-w-0 text-label-sm text-text-strong-950'>
                          {дело.заголовок}
                        </span>
                        <RiArrowRightSLine className='size-5 shrink-0 text-text-soft-400 transition duration-200 ease-out group-hover:text-text-sub-600' />
                      </div>
                      {/* Число — главное в карточке, и набрано оно крупно. Единица рядом
                          обычным кеглем: «14» без слова человек читает как сумму, а сумма в
                          карточке своя и стоит ниже. */}
                      <div className='flex items-baseline gap-2'>
                        <span className='text-title-h5 tabular-nums text-text-strong-950'>
                          {дело.сколько}
                        </span>
                        <span className='text-paragraph-sm text-text-sub-600'>
                          {pluralRu(дело.сколько, дело.единица)}
                        </span>
                      </div>
                      <p className='flex-1 text-paragraph-sm text-text-sub-600'>{дело.пояснение}</p>
                      {дело.сумма === null ? null : (
                        // Сумма отделена чертой и прижата к низу карточки: у трёх карточек в
                        // ряду суммы оказываются на одной линии и сравниваются взглядом, даже
                        // когда пояснения разной длины.
                        <div className='border-t border-stroke-soft-200 pt-3'>
                          <span className='tabular-nums text-label-sm text-text-strong-950'>
                            {formatRubles(дело.сумма)}
                          </span>
                        </div>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          );
        }}
      </ScreenState>
    </main>
  );
}

/** Время среза очереди, пока данные есть. Показывается в шапке, которая живёт вне их загрузки. */
function срезДанных(данные: { generated_at: string } | undefined): Date | null {
  return данные === undefined ? null : parseMoment(данные.generated_at);
}
